import { createBrowserRouter, Navigate } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import { Loading, RouteErrorBoundary } from '@/components/common'
import { PublicRoute } from '@/components/auth'
import {
  PageLoader,
  ProtectedRoute,
  EncryptedRoute,
  AppLayout,
} from '@/components/routing'

// Lazy load pages for code splitting
const Landing = lazy(() => import('@/pages/Landing/Landing'))
const Login = lazy(() => import('@/pages/Auth/Login'))
const Register = lazy(() => import('@/pages/Auth/Register'))
const WaitingForConsultant = lazy(() => import('@/pages/Auth/WaitingForConsultant'))
const Unlock = lazy(() => import('@/pages/Auth/Unlock'))
const Dashboard = lazy(() => import('@/pages/Dashboard/Dashboard'))
const OrderList = lazy(() => import('@/pages/Orders/OrderList'))
const OrderDetail = lazy(() => import('@/pages/Orders/OrderDetail'))
const AppointmentList = lazy(() => import('@/pages/Appointments/AppointmentList'))
const AppointmentDetail = lazy(() => import('@/pages/Appointments/AppointmentDetail'))
const DocumentList = lazy(() => import('@/pages/Documents/DocumentList'))
const DocumentDetail = lazy(() => import('@/pages/Documents/DocumentDetail'))
const VideoList = lazy(() => import('@/pages/Videos/VideoList'))
const VideoPlayerPage = lazy(() => import('@/pages/Videos/VideoPlayer'))
const OfferList = lazy(() => import('@/pages/Offers/OfferList'))
const OfferDetail = lazy(() => import('@/pages/Offers/OfferDetail'))
const RecommendationList = lazy(() => import('@/pages/Recommendations/RecommendationList'))
const RequestList = lazy(() => import('@/pages/Requests/RequestList'))
const RequestDetail = lazy(() => import('@/pages/Requests/RequestDetail'))
const NewRequest = lazy(() => import('@/pages/Requests/NewRequest'))
// NOTE: Profile page removed as of 2026-02 - customer data is now local-only on consultant devices
const AvailableSlots = lazy(() => import('@/pages/TimeSlots/AvailableSlots'))
const SlotDetail = lazy(() => import('@/pages/TimeSlots/SlotDetail'))
const MyBookings = lazy(() => import('@/pages/Bookings/MyBookings'))
const LoyaltyDashboard = lazy(() => import('@/pages/Loyalty/LoyaltyDashboard'))
const PointsHistory = lazy(() => import('@/pages/Loyalty/PointsHistory'))
const RewardsPage = lazy(() => import('@/pages/Loyalty/RewardsPage'))
const RedemptionStatus = lazy(() => import('@/pages/Loyalty/RedemptionStatus'))
const ReferralPage = lazy(() => import('@/pages/Loyalty/ReferralPage'))
const ReferralLanding = lazy(() => import('@/pages/Referral/ReferralLanding'))

// Router configuration
export const router = createBrowserRouter([
  // Public landing page - accessible at root
  {
    path: '/',
    element: (
      <PublicRoute>
        <Suspense fallback={<PageLoader />}>
          <Landing />
        </Suspense>
      </PublicRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },

  // Public routes - no authentication required
  {
    path: '/login',
    element: (
      <PublicRoute>
        <Suspense fallback={<PageLoader />}>
          <Login />
        </Suspense>
      </PublicRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/register',
    element: (
      <PublicRoute>
        <Suspense fallback={<PageLoader />}>
          <Register />
        </Suspense>
      </PublicRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },

  // Protected but not encrypted routes - requires auth, not encryption unlock
  {
    path: '/waiting',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <WaitingForConsultant />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/unlock',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <Unlock />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },

  // Encrypted routes with app layout - requires auth AND encryption unlocked
  {
    path: '/dashboard',
    element: (
      <EncryptedRoute>
        <AppLayout />
      </EncryptedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <Dashboard />,
      },
    ],
  },
  {
    path: '/orders',
    element: (
      <EncryptedRoute>
        <AppLayout />
      </EncryptedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <OrderList />,
      },
      {
        path: ':id',
        element: <OrderDetail />,
      },
    ],
  },
  {
    path: '/appointments',
    element: (
      <EncryptedRoute>
        <AppLayout />
      </EncryptedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <AppointmentList />,
      },
      {
        path: ':id',
        element: <AppointmentDetail />,
      },
    ],
  },
  {
    path: '/offers',
    element: (
      <EncryptedRoute>
        <AppLayout />
      </EncryptedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <OfferList />,
      },
      {
        path: ':id',
        element: <OfferDetail />,
      },
    ],
  },
  {
    path: '/recommendations',
    element: (
      <EncryptedRoute>
        <AppLayout />
      </EncryptedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <RecommendationList />,
      },
    ],
  },
  {
    path: '/book',
    element: (
      <EncryptedRoute>
        <AppLayout />
      </EncryptedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <AvailableSlots />,
      },
      {
        path: ':id',
        element: <SlotDetail />,
      },
    ],
  },
  {
    path: '/bookings',
    element: (
      <EncryptedRoute>
        <AppLayout />
      </EncryptedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <MyBookings />,
      },
    ],
  },
  {
    path: '/requests',
    element: (
      <EncryptedRoute>
        <AppLayout />
      </EncryptedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <RequestList />,
      },
      {
        path: 'new',
        element: <NewRequest />,
      },
      {
        path: ':id',
        element: <RequestDetail />,
      },
    ],
  },
  {
    path: '/documents',
    element: (
      <EncryptedRoute>
        <AppLayout />
      </EncryptedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <DocumentList />,
      },
      {
        path: ':id',
        element: <DocumentDetail />,
      },
    ],
  },
  {
    path: '/videos',
    element: (
      <EncryptedRoute>
        <AppLayout />
      </EncryptedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <VideoList />,
      },
      {
        path: ':videoId',
        element: <VideoPlayerPage />,
      },
    ],
  },
  // Loyalty routes
  {
    path: '/loyalty',
    element: (
      <EncryptedRoute>
        <AppLayout />
      </EncryptedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <LoyaltyDashboard />,
      },
      {
        path: 'history',
        element: <PointsHistory />,
      },
      {
        path: 'rewards',
        element: <RewardsPage />,
      },
      {
        path: 'redemptions',
        element: <RedemptionStatus />,
      },
      {
        path: 'referrals',
        element: <ReferralPage />,
      },
    ],
  },

  // Public referral landing page - no auth required
  {
    path: '/r/:code',
    element: (
      <Suspense fallback={<PageLoader />}>
        <ReferralLanding />
      </Suspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },

  // NOTE: /profile route removed as of 2026-02 - customer profile data is now local-only
  // Redirect any legacy /profile links to dashboard
  {
    path: '/profile',
    element: <Navigate to="/dashboard" replace />,
  },
  {
    path: '/profile/*',
    element: <Navigate to="/dashboard" replace />,
  },

  // OAuth callback route
  {
    path: '/oauth/callback',
    element: (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loading size="lg" text="Completing sign in..." />
      </div>
    ),
    errorElement: <RouteErrorBoundary />,
  },

  // Catch-all - show error boundary for unknown routes
  {
    path: '*',
    element: <RouteErrorBoundary />,
  },
])

export default router
